// dependencies
import React, { PropsWithChildren, useRef } from "react";

// components
import GridToolPanel, { MenuOption } from "./Panel";

// styles
import css from "./GridWithToolPanel.module.scss";

type AgThemes =
	| "ag-theme-alpine"
	| "ag-theme-alpine-dark"
	| "ag-theme-balham"
	| "ag-theme-balham-dark"
	| "ag-theme-material";

interface BaseProps {
	/**
	 * The theme to use for the grid, these are provided by ag-grid
	 * @default ag-theme-alpine
	 */
	theme?: AgThemes;
	/**
	 * The options to display in the tool panel.
	 * @see MenuOption
	 */
	menuOptions: MenuOption[] | [];
	/**
	 * The initial width of the tool panel. The panel is resizable by the user.
	 * @default 200
	 */
	panelInitialWidth?: number;
	/**
	 * The id of the panel to open initially. If null, no panel will be open.
	 * @default null
	 */
	initialOpenPanel?: string;
}

/**
 * The component renders a grid and a tool panel side by side. Resizing the
 * tool panel is done by dragging the panel, this will resize the grid so that
 * the user can scroll the grid next to the tool panel rather than the tool
 * panel covering the grid. The grid is passed in as a child component.
 */
const GridWithToolPanel = (props: PropsWithChildren<BaseProps>) => {
	const {
		menuOptions,
		children,
		panelInitialWidth = 200,
		theme = "ag-theme-alpine",
		initialOpenPanel = null,
	} = props;

	// these are refs to access the overall parent and grid parent. We need to
	// access these to update the widths of the grid and panel and set the
	// width of the grid.
	const gridRef = useRef<HTMLDivElement>(null);
	const parentRef = useRef<HTMLDivElement>(null);

	return (
		<div ref={parentRef} className={css.main}>
			<div ref={gridRef} className={`${css.grid} ${theme}`}>
				{children}
			</div>
			<GridToolPanel
				menuOptions={menuOptions}
				gridRef={gridRef}
				parentRef={parentRef}
				panelInitialWidth={panelInitialWidth}
				initialOpenPanel={initialOpenPanel}
			/>
		</div>
	);
};

export default GridWithToolPanel;
